import React, {useEffect} from 'react';
import {Form, Input, Modal, message} from 'antd';
import {useQuery, useMutation} from '@apollo/client';
import {
    CREATE_BCP_CHECK_USER_GROUP,
    UPDATE_BCP_CHECK_USER_GROUP,
} from '../graphql/mutations/bcpCheckUserGroupMutations';
import {BCP_CHECK_USER_GROUP} from '../graphql/queries/bcpCheckUserGroupQueries';
import {CheckUserGroupDetailResponse} from '../graphql/types/BcpCheckUserGroupTypes';

interface BcpCheckUserGroupFormModalProps {
    visible: boolean;
    editGroupId?: number;
    onSuccess: () => void;
    onCancel: () => void;
}

const BcpCheckUserGroupFormModal: React.FC<BcpCheckUserGroupFormModalProps> = ({
    visible,
    editGroupId,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const isEdit = Boolean(editGroupId);

    const {data: detailData} = useQuery<CheckUserGroupDetailResponse>(BCP_CHECK_USER_GROUP, {
        variables: {id: editGroupId},
        skip: !isEdit || !visible || !editGroupId,
    });

    const [createGroup, {loading: creating}] = useMutation(CREATE_BCP_CHECK_USER_GROUP);
    const [updateGroup, {loading: updating}] = useMutation(UPDATE_BCP_CHECK_USER_GROUP);

    useEffect(() => {
        if (visible) {
            if (isEdit && detailData?.bcpCheckUserGroup?.data) {
                const record = detailData.bcpCheckUserGroup.data;
                form.setFieldsValue({
                    groupName: record.groupName,
                    usersText: record.users?.join(', ') ?? '',
                    remark: record.remark,
                });
            } else if (!isEdit) {
                form.resetFields();
            }
        }
    }, [visible, isEdit, detailData, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const usersText: string = values.usersText || '';
            const users = usersText
                .split(',')
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0);
            const input = {
                groupName: values.groupName,
                remark: values.remark || undefined,
                users: users.length > 0 ? users : undefined,
            };

            if (isEdit && editGroupId) {
                const res = await updateGroup({variables: {id: editGroupId, input}});
                const result = res.data?.updateBcpCheckUserGroup;
                if (!result?.success) {
                    message.error(result?.message || '更新失败');
                    return;
                }
                message.success('更新成功');
            } else {
                const res = await createGroup({variables: {input}});
                const result = res.data?.createBcpCheckUserGroup;
                if (!result?.success) {
                    message.error(result?.message || '创建失败');
                    return;
                }
                message.success('创建成功');
            }
            onSuccess();
        } catch (e: any) {
            message.error(e?.message || '操作失败');
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={isEdit ? '编辑用户组' : '新建用户组'}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={creating || updating}
            okText="保存"
            cancelText="取消"
            width={520}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="用户组名称"
                    name="groupName"
                    rules={[{required: true, message: '请输入用户组名称'}]}
                >
                    <Input placeholder="请输入用户组名称" />
                </Form.Item>
                <Form.Item
                    label="用户列表"
                    name="usersText"
                    rules={[{required: true, message: '请输入用户列表'}]}
                    extra="多个用户以英文逗号分隔，如：alice,bob,charlie"
                >
                    <Input.TextArea rows={3} placeholder="alice,bob,charlie" />
                </Form.Item>
                <Form.Item label="备注" name="remark">
                    <Input.TextArea rows={2} placeholder="请输入备注" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BcpCheckUserGroupFormModal;
